import React, {useEffect, useState, useRef} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Popper,
    TextField,
    Tooltip,
    Typography,
    ClickAwayListener
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
    createChatMessageTemplate,
    updateChatMessageTemplate,
    deleteChatMessageTemplate,
    getChatMessageTemplates
} from '../../http/chatAPI';

const MessageTemplates = ({messageValue, onInsertTemplate}) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [newTitle, setNewTitle] = useState('');
    const [newMessage, setNewMessage] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [showWarning, setShowWarning] = useState(false);
    const [pendingTemplate, setPendingTemplate] = useState(null);
    const [showPopover, setShowPopover] = useState(false);
    const anchorRef = useRef(null);

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const data = await getChatMessageTemplates();
            setTemplates(data || []);
        } catch (error) {
            console.error('Failed to load templates:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTemplates();
    }, []);

    const handleOpenDialog = (template = null) => {
        if (template) {
            setEditingTemplate(template);
            setNewTitle(template.title);
            setNewMessage(template.message);
        } else {
            setEditingTemplate(null);
            setNewTitle('');
            setNewMessage('');
        }
        setOpenDialog(true);
        setShowPopover(false);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingTemplate(null);
        setNewTitle('');
        setNewMessage('');
    };

    const handleSave = async () => {
        if (!newTitle.trim() || !newMessage.trim()) return;
        setSaving(true);
        try {
            if (editingTemplate) {
                await updateChatMessageTemplate(editingTemplate.id, newTitle.trim(), newMessage.trim());
            } else {
                await createChatMessageTemplate(newTitle.trim(), newMessage.trim());
            }
            await loadTemplates();
            handleCloseDialog();
        } catch (error) {
            const action = editingTemplate ? 'обновить' : 'создать';
            alert(`Не удалось ${action} шаблон: ` + (error?.response?.data?.message || error.message));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (templateId) => {
        if (!window.confirm('Удалить шаблон?')) return;
        setDeleting(templateId);
        try {
            await deleteChatMessageTemplate(templateId);
            setTemplates(prev => prev.filter(t => t.id !== templateId));
        } catch (error) {
            alert('Не удалось удалить шаблон: ' + (error?.response?.data?.message || error.message));
        } finally {
            setDeleting(null);
        }
    };

    const handleTemplateClick = (template) => {
        if (messageValue && messageValue.trim()) {
            setPendingTemplate(template);
            setShowWarning(true);
        } else {
            onInsertTemplate(template.message);
        }
    };

    const handleCopyToClipboard = async (e, text) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(text);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };

    const handleWarningConfirm = () => {
        if (pendingTemplate) {
            onInsertTemplate(pendingTemplate.message);
        }
        setShowWarning(false);
        setPendingTemplate(null);
    };

    const handleWarningCancel = () => {
        setShowWarning(false);
        setPendingTemplate(null);
    };

    return (
        <Box>
            <Box
                ref={anchorRef}
                onClick={() => setShowPopover(prev => !prev)}
                sx={{
                    display: 'inline-block',
                    cursor: 'pointer',
                    position: 'relative'
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: 'primary.main',
                        textDecoration: 'underline',
                        userSelect: 'none',
                        '&:hover': {
                            color: 'primary.dark'
                        }
                    }}
                >
                    Шаблоны
                </Typography>
            </Box>

            <Popper
                open={showPopover}
                anchorEl={anchorRef.current}
                placement="top-start"
                style={{zIndex: 1300}}
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 8],
                        },
                    },
                ]}
            >
                <ClickAwayListener onClickAway={() => setShowPopover(false)}>
                    <Paper
                        sx={{
                            maxWidth: 400,
                            minWidth: 250,
                            maxHeight: 400,
                            overflow: 'auto',
                            boxShadow: 3,
                            border: '1px solid #e0e0e0'
                        }}
                    >
                        {loading ? (
                            <Box sx={{display: 'flex', justifyContent: 'center', p: 2}}>
                                <CircularProgress size={24}/>
                            </Box>
                        ) : (
                            <>
                                <List dense sx={{py: 0}}>
                                    {templates.length === 0 ? (
                                        <ListItem>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2" color="text.secondary">
                                                        Нет шаблонов
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ) : (
                                        templates.map((template) => (
                                            <Tooltip
                                                key={template.id}
                                                title={
                                                    <Box sx={{maxWidth: 400}}>
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{
                                                                fontWeight: 'bold', 
                                                                display: 'block', 
                                                                mb: 0.5
                                                            }}
                                                        >
                                                            {template.title}
                                                        </Typography>
                                                        <Typography 
                                                            variant="caption" 
                                                            component="pre"
                                                            sx={{
                                                                whiteSpace: 'pre-wrap',
                                                                wordBreak: 'break-word',
                                                                fontFamily: 'inherit',
                                                                margin: 0
                                                            }}
                                                        >
                                                            {template.message}
                                                        </Typography>
                                                    </Box>
                                                }
                                                arrow
                                                placement="left"
                                                enterDelay={300}
                                            >
                                                <ListItem
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: 'action.hover'
                                                        },
                                                        borderBottom: '1px solid #f0f0f0',
                                                        py: 1
                                                    }}
                                                    secondaryAction={
                                                        <Box sx={{display: 'flex', gap: 0.5}}>
                                                            <Tooltip title="Копировать">
                                                                <IconButton
                                                                    edge="end"
                                                                    size="small"
                                                                    onClick={(e) => handleCopyToClipboard(e, template.message)}
                                                                >
                                                                    <ContentCopyIcon fontSize="small"/>
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Редактировать">
                                                                <IconButton
                                                                    edge="end"
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleOpenDialog(template);
                                                                    }}
                                                                >
                                                                    <EditIcon fontSize="small"/>
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Удалить">
                                                                <IconButton
                                                                    edge="end"
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDelete(template.id);
                                                                    }}
                                                                    disabled={deleting === template.id}
                                                                >
                                                                    {deleting === template.id ? (
                                                                        <CircularProgress size={16}/>
                                                                    ) : (
                                                                        <DeleteIcon fontSize="small"/>
                                                                    )}
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    }
                                                    onClick={() => {
                                                        handleTemplateClick(template);
                                                        setShowPopover(false);
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body2" sx={{fontWeight: 500}}>
                                                                {template.title}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'pre-wrap'
                                                                }}
                                                            >
                                                                {template.message}
                                                            </Typography>
                                                        }
                                                        sx={{pr: 12}}
                                                    />
                                                </ListItem>
                                            </Tooltip>
                                        ))
                                    )}
                                </List>
                                <Box
                                    sx={{
                                        borderTop: '2px solid #e0e0e0',
                                        position: 'sticky',
                                        bottom: 0,
                                        backgroundColor: '#fff'
                                    }}
                                >
                                    <Button
                                        fullWidth
                                        startIcon={<AddIcon/>}
                                        onClick={() => handleOpenDialog()}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            px: 2,
                                            py: 1,
                                            textTransform: 'none',
                                            fontWeight: 500
                                        }}
                                    >
                                        Создать шаблон
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Paper>
                </ClickAwayListener>
            </Popper>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingTemplate ? 'Редактировать шаблон' : 'Новый шаблон'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Название"
                        fullWidth
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        sx={{mb: 2}}
                    />
                    <TextField
                        margin="dense"
                        label="Текст сообщения"
                        fullWidth
                        multiline
                        rows={4}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Отмена</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={!newTitle.trim() || !newMessage.trim() || saving}
                    >
                        {saving ? <CircularProgress size={20}/> : (editingTemplate ? 'Сохранить' : 'Создать')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Warning Dialog */}
            <Dialog open={showWarning} onClose={handleWarningCancel}>
                <DialogTitle>Внимание</DialogTitle>
                <DialogContent>
                    <Typography>
                        Поле ввода не пустое. Вставка шаблона заменит текущий текст. Продолжить?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleWarningCancel}>Отмена</Button>
                    <Button onClick={handleWarningConfirm} variant="contained">
                        Продолжить
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MessageTemplates;
